import { memo, useEffect, useContext, useMemo, useState } from 'react'
import { Box, FlexGrid } from '@telus-uds/components-web'
import { object, bool, number } from 'prop-types'
import { navigateToUrl } from 'single-spa'
import { Sticky } from 'react-sticky'
import { useCookies } from 'react-cookie'
import { useSearchParam, useMedia } from 'react-use'
import PriceSummary from './components/organisms/PriceSummary'
import localeContext from './contexts/Locale'
import useTranslate from './hooks/useTranslate'
import PageHeading from './components/organisms/PageHeading'
import PriceSummaryTopActions from './components/molecules/PriceSummaryTopActions'
import PriceSummaryBottomActions from './components/molecules/PriceSummaryBottomActions'
import AppNotification from './components/organisms/AppNotification'
import {
  FlexContainerLimitWidth,
  StyledStickyContainer,
  StyledStickyItem,
  StyledHeadingContainer
} from './components/organisms/StyledContainers'
import HeaderFooter from './components/organisms/HeaderFooter'
import Addon from './components/organisms/Addon'
import CodeRedemption from './components/organisms/CodeRedemption'
import HomePhone from './components/organisms/HomePhone'
import { SmartHomeSecurity, SecureBusiness } from './components/organisms/Security'
import Gift from './components/organisms/Gift'
import BusinessConnect from './components/organisms/BusinessConnect'
import Byod from './components/organisms/Byod'
import ConnectedDevice from './components/organisms/MobilityConnectedDevice/ConnectedDevice'
import MobilityInternet from './components/organisms/MobilityConnectedDevice/MobilityInternet'
import StreamPlus from './components/organisms/StreamPlus'
import PikTV from './components/organisms/PikTV'
import LivingWellCompanion from './components/organisms/LivingWellCompanion'
import Internet from './components/organisms/Internet'
import ValueProps from './components/organisms/ValueProps'
import OptikTV from './components/organisms/OptikTV'
import { alloyEvents } from './utils/alloyAnalytics'
import { onPersonalizationCallError, onEmptyCart, onPageLoadEvent } from './utils/legacyAnalytics'
import MobilityAccessory from './components/organisms/MobilityAccessory'
import { OrbitTablet } from './components/organisms/MobilityConnectedDevice'
import FullPageSpinner from './components/organisms/Spinner'
import { getNumberOfCartModuleRendered } from './utils/ui'

import {
  MobilityPhone,
  MobilityWirelessHomePhone,
  OrbitPhone
} from './components/organisms/MobilityPhone'
import NonIndexedLegals from './components/molecules/NonIndexedLegals/NonIndexedLegals'
import useLegacyAnalytics from './hooks/useLegacyAnalytics'
import { getBackorderedItems, getOutOfStockItems } from './utils/rti'
import { dataLayerRTIQueue } from './utils/legacyAnalytics/dataLayerRTIQueue'
import useRTISessionStorage from './hooks/useRTISessionStorage'
import { removeDuplicateVisitorStateCookie } from './utils/cart'
import PAPMessage from './components/molecules/PAPMessage'

const Cart = ({ cart, isRemovingCartItems, isResettingCart, isAuthenticated, featureFlags }) => {
  const t = useTranslate()
  const locale = useContext(localeContext)
  const isMobile = useMedia('(max-width: 767px)')
  const { updateLegacyDataLayer } = useLegacyAnalytics()
  const [hasVisitorStateCookie, setHasVisitorStateCookie] = useState({
    status: false,
    error: undefined
  })
  const [, , removeCookie] = useCookies()
  const backorderedItems = getBackorderedItems(cart)
  const outOfStockItems = getOutOfStockItems(cart)
  useRTISessionStorage(cart)
  const linkType = useSearchParam('linktype')

  const isCartEmpty = useMemo(
    () => getNumberOfCartModuleRendered(cart, featureFlags) === 0,
    [cart, featureFlags]
  )

  useEffect(() => {
    // visitorState cookie from personalization api needs to be set on page load before we can call analytics
    if (hasVisitorStateCookie.status) {
      // having duplicate visitorState cookies causes issues with analytics, we should always have 1 visitorState cookie
      removeDuplicateVisitorStateCookie(removeCookie, document.cookie)

      if (getNumberOfCartModuleRendered(cart, featureFlags) > 0) {
        updateLegacyDataLayer(onPageLoadEvent(linkType))
        alloyEvents.pageLoaded({ cart, locale, isAuthenticated, linkType })
      }
    }
  }, [hasVisitorStateCookie])

  useEffect(() => {
    // when there is an error calling personalization api to use ab/test
    if (hasVisitorStateCookie.error) {
      const { error } = hasVisitorStateCookie
      alloyEvents.toolError(error)
      // delaying the update legacy data layer to allow page loaded event to be tracked first
      setTimeout(() => {
        const errorMessage = hasVisitorStateCookie.error?.message
        updateLegacyDataLayer(onPersonalizationCallError(errorMessage))
      }, 6000)
    }
  }, [hasVisitorStateCookie])

  useEffect(() => {
    if (hasVisitorStateCookie.status) {
      dataLayerRTIQueue(backorderedItems, outOfStockItems, updateLegacyDataLayer)
    }
  }, [backorderedItems, outOfStockItems, hasVisitorStateCookie])

  if (isCartEmpty) {
    updateLegacyDataLayer(onEmptyCart)
    alloyEvents.storageEvents.emptyCart({ cart, locale, isAuthenticated })
    navigateToUrl(`/${locale.lang}/commerce/notices/empty-cart`)
    return <FullPageSpinner tip={t('spinner')} />
  }

  return (
    <FlexContainerLimitWidth>
      <HeaderFooter />
      {(Boolean(isRemovingCartItems) || Boolean(isResettingCart)) && (
        <FullPageSpinner tip={t('spinner')} />
      )}
      <FlexGrid limitWidth={false}>
        <FlexGrid.Row>
          <FlexGrid.Col>
            <StyledHeadingContainer>
              <PageHeading />
              <Box>
                <AppNotification
                  isAuthenticated={isAuthenticated}
                  setHasVisitorStateCookie={setHasVisitorStateCookie}
                />
              </Box>
            </StyledHeadingContainer>
          </FlexGrid.Col>
        </FlexGrid.Row>
        <FlexGrid.Row>
          <FlexGrid.Col lg={8} xs={12}>
            <Box>
              <MobilityPhone />
              <OrbitPhone />
              <Byod />
              <ConnectedDevice />
              <OrbitTablet />
              <MobilityAccessory />
              <MobilityInternet />
              <MobilityWirelessHomePhone />
              <Gift />
              <Internet />
              <OptikTV />
              <PikTV />
              <SmartHomeSecurity />
              <Addon />
              <HomePhone />
              <BusinessConnect />
              <SecureBusiness />
              <StreamPlus />
              <LivingWellCompanion />
            </Box>
            <NonIndexedLegals />
          </FlexGrid.Col>
          <FlexGrid.Col lg={4} xs={12}>
            <StyledStickyContainer>
              <Sticky>
                {({ style, isSticky }) => (
                  <StyledStickyItem style={style} isSticky={isSticky}>
                    <CodeRedemption />
                    {featureFlags?.papMessage && isMobile && <PAPMessage />}
                    <PriceSummary
                      cart={cart}
                      isAuthenticated={isAuthenticated}
                      featureFlags={featureFlags}
                      TopActions={<PriceSummaryTopActions />}
                      BottomActions={<PriceSummaryBottomActions />}
                    />
                  </StyledStickyItem>
                )}
              </Sticky>
            </StyledStickyContainer>
          </FlexGrid.Col>
        </FlexGrid.Row>
        <FlexGrid.Row>
          <FlexGrid.Col>
            <ValueProps />
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>
    </FlexContainerLimitWidth>
  )
}

Cart.propTypes = {
  cart: object.isRequired,
  isRemovingCartItems: number.isRequired,
  isResettingCart: number.isRequired,
  isAuthenticated: bool.isRequired,
  featureFlags: object.isRequired
}

export default memo(Cart)
